import React, { useEffect, useRef } from "react";
import {
  ArrowUpRight,
  Crosshair,
  MessageSquare,
  Pencil,
  X
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { MetaLine } from "@/components/ui/meta-line";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { fieldSourceAttribution } from "./attribution";
import {
  formatFieldLine,
  isIdentifierField,
  parseFieldLine,
  type FieldLine
} from "./fieldLine";
import { formatFieldValue } from "./fieldValue";
import { SourceEvidence } from "./SourceEvidence";
import { SOURCE_KINDS, fieldSourceKindOf, sourceEntryOf } from "./sourceKinds";
import type {
  FieldSourceItem,
  OpenFormCitation,
  ResolveFieldSourceUser
} from "./types";

type FieldSourceCardBaseProps = {
  item: FieldSourceItem;
  /** Names the actor a provenance entry only knows by uid (the case agent's principal). */
  resolveUser?: ResolveFieldSourceUser;
  /** The field whose citation the docked panel is showing. */
  openCitationFieldName?: string | null;
  onToggleCitation?: (citation: OpenFormCitation) => void;
  /** "Open Source" / "Open Intake" / "Open Profile" / "Open Filing Rules". */
  onOpenSource?: () => void;
  onComment?: () => void;
  onEditValue?: () => void;
  /** Set the value in mono; detected from the field's name when omitted. */
  mono?: boolean;
  className?: string;
};

type FieldSourceHoverCardProps = FieldSourceCardBaseProps & {
  /** Pinned by a click on the field: shows a close button. */
  pinned?: boolean;
  /** Close (the close button, or Esc). */
  onClose?: () => void;
};

type FieldSourceRowProps = FieldSourceCardBaseProps & {
  selected?: boolean;
  onSelect?: () => void;
  /** Scroll the page to the field. */
  onLocate?: () => void;
  /** Lead the header with the part; off inside a list grouped by part. */
  showPart?: boolean;
};

export type FieldSourceCardProps =
  | ({ variant: "hover" } & FieldSourceHoverCardProps)
  | ({ variant: "row" } & FieldSourceRowProps);

type Resolved = {
  fieldLine: FieldLine;
  headerLabel: string | null;
  label: string;
  instruction: string | null;
  value: string;
  mono: boolean;
  isEdited: boolean;
  hasCaution: boolean;
};

function resolve(
  item: FieldSourceItem,
  { showPart, mono }: { showPart: boolean; mono?: boolean }
): Resolved {
  const fieldLine = parseFieldLine(item.pdfLabel);
  const headerLabel =
    formatFieldLine(showPart ? fieldLine : { ...fieldLine, part: null }) ??
    (typeof item.pageNumber === "number" ? `Page ${item.pageNumber}` : null);
  const formText = String(item.formText || "").trim();
  const question = String(item.question || "").trim();
  const label =
    formText ||
    String(item.label || "").trim() ||
    fieldLine.name ||
    question ||
    item.fieldName;
  const source = sourceEntryOf(item.provenance);
  return {
    fieldLine,
    headerLabel,
    label,
    instruction: formText || !question || question === label ? null : question,
    value: formatFieldValue(item.value),
    mono: mono ?? isIdentifierField(item.fieldName, item.pdfLabel, item.label),
    isEdited: item.provenance?.kind === "manual_edit",
    hasCaution: Boolean(
      String(
        source?.source_caution || item.provenance?.source_caution || ""
      ).trim()
    )
  };
}

function KindTag({ item }: { item: FieldSourceItem }) {
  if (!item.provenance) return null;
  const kind = fieldSourceKindOf(item.provenance);
  // An edit with no source behind it says so with its Edited badge alone.
  if (kind === "manual") return null;
  const spec = SOURCE_KINDS[kind];
  const Icon = spec.icon;
  return (
    <Tag data-testid="field-source-kind">
      <Icon className={spec.hue} aria-hidden />
      {spec.label}
    </Tag>
  );
}

function StateBadges({ resolved }: { resolved: Resolved }) {
  return (
    <>
      {resolved.isEdited ? <StatusBadge tone="warn">Edited</StatusBadge> : null}
      {resolved.hasCaution ? (
        <StatusBadge tone="warn">Check Source</StatusBadge>
      ) : null}
    </>
  );
}

/** Spans only: in the row variant this sits inside the selection button. */
function LabelAndValue({ resolved }: { resolved: Resolved }) {
  const label = (
    <span
      className={cn(
        "w-fit max-w-full text-base text-fg-2 wrap-break-word",
        resolved.instruction && "cursor-help"
      )}
    >
      {resolved.label}
    </span>
  );
  return (
    <span className="flex min-w-0 flex-col gap-0.5 text-left">
      {resolved.instruction ? (
        <Tooltip>
          <TooltipTrigger asChild>{label}</TooltipTrigger>
          <TooltipContent side="top" align="start">
            {resolved.instruction}
          </TooltipContent>
        </Tooltip>
      ) : (
        label
      )}
      <span
        className={cn(
          "text-md font-medium whitespace-pre-wrap wrap-break-word text-fg",
          resolved.mono && "font-mono"
        )}
        data-testid="field-source-value"
      >
        {resolved.value || "-"}
      </span>
    </span>
  );
}

function Attribution({
  item,
  resolveUser
}: {
  item: FieldSourceItem;
  resolveUser?: ResolveFieldSourceUser;
}) {
  const attribution = fieldSourceAttribution(item.provenance, { resolveUser });
  if (!attribution) return null;
  const { avatar } = attribution;
  return (
    <div
      className="flex min-w-0 items-center gap-1.5"
      data-testid="field-source-attribution"
    >
      {avatar?.kind === "person" ? (
        <Avatar size="2xs">
          {avatar.imageUrl ? (
            <AvatarImage src={avatar.imageUrl} alt="" draggable={false} />
          ) : null}
          <AvatarFallback>{avatar.initials}</AvatarFallback>
        </Avatar>
      ) : avatar?.kind === "agent" ? (
        <span role="img" aria-label="Casey" className="inline-flex">
          <AgentMoodAvatar size="xs" agent={{ key: "assistant" }} />
        </span>
      ) : null}
      <MetaLine parts={attribution.parts} />
    </div>
  );
}

function HoverCard({
  item,
  resolveUser,
  openCitationFieldName,
  onToggleCitation,
  onOpenSource,
  onComment,
  onEditValue,
  mono,
  className,
  pinned = false,
  onClose
}: FieldSourceHoverCardProps) {
  const resolved = resolve(item, { showPart: true, mono });
  const kind = item.provenance ? fieldSourceKindOf(item.provenance) : null;
  const openLabel = kind ? SOURCE_KINDS[kind].openLabel : "Open Source";
  const hasSource = Boolean(sourceEntryOf(item.provenance));
  const actions = [
    onOpenSource && hasSource ? (
      <Button key="open" variant="ghost" size="xs" onClick={onOpenSource}>
        <ArrowUpRight aria-hidden />
        {openLabel}
      </Button>
    ) : null,
    onComment ? (
      <Button key="comment" variant="ghost" size="xs" onClick={onComment}>
        <MessageSquare aria-hidden />
        Comment
      </Button>
    ) : null,
    onEditValue ? (
      <Button key="edit" variant="ghost" size="xs" onClick={onEditValue}>
        <Pencil aria-hidden />
        Edit Value
      </Button>
    ) : null
  ].filter(Boolean);

  return (
    <div
      data-slot="field-source-card"
      data-variant="hover"
      className={cn(
        "flex w-90 max-w-full flex-col overflow-hidden rounded-lg bg-elevated text-left shadow-popover ring-1 ring-line-3",
        className
      )}
      onKeyDown={(event) => {
        if (event.key === "Escape" && onClose) {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex flex-col gap-2.5 p-3">
        <div className="flex min-h-5 min-w-0 items-center gap-1.5">
          {resolved.headerLabel ? (
            <span className="shrink-0 text-sm text-fg-3">
              {resolved.headerLabel}
            </span>
          ) : null}
          <span className="flex-1" />
          <StateBadges resolved={resolved} />
          <KindTag item={item} />
          {pinned && onClose ? (
            <Button
              variant="ghost"
              size="icon-2xs"
              className="-mr-1"
              onClick={onClose}
              aria-label="Close"
            >
              <X aria-hidden />
            </Button>
          ) : null}
        </div>
        <LabelAndValue resolved={resolved} />
        <SourceEvidence
          item={item}
          variant="full"
          mono={resolved.mono}
          openCitationFieldName={openCitationFieldName}
          onToggleCitation={onToggleCitation}
        />
        <Attribution item={item} resolveUser={resolveUser} />
      </div>
      {actions.length ? (
        <div className="flex items-center gap-0.5 border-t border-line-1 px-1.5 py-1">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

function SourceRow({
  item,
  resolveUser,
  openCitationFieldName,
  onToggleCitation,
  onOpenSource,
  onComment,
  onEditValue,
  mono,
  className,
  selected = false,
  onSelect,
  onLocate,
  showPart = false
}: FieldSourceRowProps) {
  const resolved = resolve(item, { showPart, mono });
  const rowRef = useRef<HTMLDivElement>(null);
  const kind = item.provenance ? fieldSourceKindOf(item.provenance) : null;
  const openLabel = kind ? SOURCE_KINDS[kind].openLabel : "Open Source";
  const hasSource = Boolean(sourceEntryOf(item.provenance));

  // Selecting the field on the page brings its row into view.
  useEffect(() => {
    if (selected) rowRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [selected]);

  const actions = [
    onLocate ? (
      <Button
        key="locate"
        variant="ghost"
        size="icon-xs"
        onClick={onLocate}
        aria-label="Locate on Form"
        title="Locate on Form"
      >
        <Crosshair aria-hidden />
      </Button>
    ) : null,
    onOpenSource && hasSource ? (
      <Button
        key="open"
        variant="ghost"
        size="icon-xs"
        onClick={onOpenSource}
        aria-label={openLabel}
        title={openLabel}
      >
        <ArrowUpRight aria-hidden />
      </Button>
    ) : null,
    onComment ? (
      <Button
        key="comment"
        variant="ghost"
        size="icon-xs"
        onClick={onComment}
        aria-label="Comment"
        title="Comment"
      >
        <MessageSquare aria-hidden />
      </Button>
    ) : null,
    onEditValue ? (
      <Button
        key="edit"
        variant="ghost"
        size="icon-xs"
        onClick={onEditValue}
        aria-label="Edit Value"
        title="Edit Value"
      >
        <Pencil aria-hidden />
      </Button>
    ) : null
  ].filter(Boolean);

  return (
    <div
      ref={rowRef}
      data-slot="field-source-card"
      data-variant="row"
      data-selected={selected || undefined}
      className={cn(
        "group/row relative flex min-w-0 flex-col gap-1.5 px-3 py-2.5 transition-colors duration-100",
        "has-[[data-slot=field-source-select]:focus-visible]:ring-2 has-[[data-slot=field-source-select]:focus-visible]:ring-primary/40 has-[[data-slot=field-source-select]:focus-visible]:ring-inset",
        selected ? "bg-fill-3" : "hover:bg-fill-1",
        className
      )}
    >
      <button
        type="button"
        data-slot="field-source-select"
        aria-pressed={selected}
        onClick={onSelect}
        className="flex w-full min-w-0 cursor-pointer flex-col gap-1.5 text-left outline-none"
      >
        <span className="flex min-h-5 w-full min-w-0 items-center gap-1.5">
          {resolved.headerLabel ? (
            <span className="shrink-0 text-sm text-fg-3">
              {resolved.headerLabel}
            </span>
          ) : null}
          <span className="flex-1" />
          <StateBadges resolved={resolved} />
          {/* The row actions take the tag's place on hover and focus. */}
          <span
            className={cn(
              "flex shrink-0 justify-end",
              actions.length &&
                "group-focus-within/row:invisible group-hover/row:invisible"
            )}
            style={
              actions.length ? { minWidth: actions.length * 26 } : undefined
            }
          >
            <KindTag item={item} />
          </span>
        </span>
        <LabelAndValue resolved={resolved} />
      </button>
      <SourceEvidence
        item={item}
        variant="source-row"
        openCitationFieldName={openCitationFieldName}
        onToggleCitation={onToggleCitation}
      />
      <Attribution item={item} resolveUser={resolveUser} />
      {actions.length ? (
        <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 transition-opacity duration-100 group-focus-within/row:opacity-100 group-hover/row:opacity-100">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One filled field and where its value came from.
 *
 * `hover` is the popover over a form field: the source cropped to the value,
 * who filled it and when, and Open, Comment and Edit Value. `row` is the same
 * body as a Sources panel row: the selection target is a real button over the
 * header, label and value, and the evidence collapses to its source row, so
 * nothing interactive is nested.
 */
export function FieldSourceCard({ variant, ...rest }: FieldSourceCardProps) {
  return variant === "hover" ? (
    <HoverCard {...(rest as FieldSourceHoverCardProps)} />
  ) : (
    <SourceRow {...(rest as FieldSourceRowProps)} />
  );
}
